import React from 'react';
import { Card, Row, Col, Typography, Statistic, Progress } from 'antd';
import { CarOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Top5Component = ({ rightSwipesData, embedded = false, fixedHeight }) => {
  // Calculate dealer response metrics
  const totalLeads = rightSwipesData.length;
  
  // Count responses received (any stage beyond "Prospect Waiting")
  const responsesReceived = rightSwipesData.filter(item => 
    item.currentStage !== 'Prospect Waiting'
  ).length;
  
  // Count responses not received (still in "Prospect Waiting")
  const responsesNotReceived = rightSwipesData.filter(item => 
    item.currentStage === 'Prospect Waiting'
  ).length;
  
  // Calculate lost opportunities percentage
  const lostOpportunitiesPercentage = totalLeads > 0 ? Math.round((responsesNotReceived / totalLeads) * 100) : 0;
  
  // Calculate average amount of leads (prefer numeric price from inventory, fallback to MSRP parsing)
  const avgLeadAmount = totalLeads > 0
    ? rightSwipesData.reduce((sum, item) => {
        const numeric = (typeof item.priceNumber === 'number' && isFinite(item.priceNumber))
          ? item.priceNumber
          : (item.msrp ? parseFloat(String(item.msrp).replace(/[$,]/g, '')) : 0);
        return sum + (Number.isFinite(numeric) ? numeric : 0);
      }, 0) / totalLeads
    : 0;
  
  // Calculate response rate
  const responseRate = totalLeads > 0 ? Math.round((responsesReceived / totalLeads) * 100) : 0;

  const content = (
    <>
      <div className="top5-header">
        <div className="top5-title">
          <div className="title-icon">
            <CarOutlined />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>Top 5 Analytics</Title>
            <Text type="secondary">Response metrics and lead performance</Text>
          </div>
        </div>
      </div>

      <Row gutter={[12, 12]} style={{ marginTop: '12px' }}>
        <Col xs={12} sm={6}>
          <div className="top5-stat">
            <div className="stat-icon">
              <CheckCircleOutlined />
            </div>
            <div className="stat-content">
              <Statistic
                title="Response Received"
                value={responsesReceived}
                valueStyle={{ color: '#52c41a', fontSize: '20px' }}
              />
            </div>
          </div>
        </Col>
        
        <Col xs={12} sm={6}>
          <div className="top5-stat">
            <div className="stat-icon">
              <CloseCircleOutlined />
            </div>
            <div className="stat-content">
              <Statistic
                title="Not Received"
                value={responsesNotReceived}
                valueStyle={{ color: '#ff4d4f', fontSize: '20px' }}
              />
            </div>
          </div>
        </Col>
        
        <Col xs={12} sm={6}>
          <div className="top5-stat">
            <div className="stat-icon">
              <ExclamationCircleOutlined />
            </div>
            <div className="stat-content">
              <Statistic
                title="Lost Opportunities"
                value={lostOpportunitiesPercentage}
                suffix="%"
                valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
              />
            </div>
          </div>
        </Col>
        
        <Col xs={12} sm={6}>
          <div className="top5-stat">
            <div className="stat-content">
              <div className="response-rate">
                <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                  {responseRate}%
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Response Rate
                </Text>
              </div>
              <Progress 
                percent={responseRate} 
                size="small" 
                strokeColor="#52c41a"
                showInfo={false}
                style={{ marginTop: '4px' }}
              />
            </div>
          </div>
        </Col>
      </Row>

      <div className="top5-summary" style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Total Leads</Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
              {totalLeads}
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Avg Lead Amount</Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
              ${Math.round(avgLeadAmount).toLocaleString()}
            </div>
          </Col>
        </Row>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div style={{
        marginTop: '12px',
        border: '1px solid #f0f0f0',
        borderRadius: 12,
        padding: 12,
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        minHeight: fixedHeight,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        {content}
      </div>
    );
  }

  return (
    <Card className="top5-card" style={{ marginTop: '24px' }}>
      {content}
    </Card>
  );
};

export default Top5Component;
